import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, ColumnContent, Stack, Text } from '@enechain/ecloud-designsystem'
import { useErrorToast, useSuccessToast } from '@enechain/eposition-ui'
import { ECLEAR_OPTION_CATEGORY } from '@enechain/eposition-ui/src/models/eclearOption'

import { FormPrimaryButton } from '~/components/common/FormPrimaryButton'
import { EVENT_NAME } from '~/constants/logEvent'
import { MarginSimulationRelationForm } from '~/pages/Simulation/components/SimulationAndResultArea/MarginSimulationSection/MarginSimulationRelationForm'
import { MarginSimulationTable } from '~/pages/Simulation/components/SimulationAndResultArea/MarginSimulationSection/ResultSection/MarginSimulationTable'
import { SimulationEmptyResult } from '~/pages/Simulation/components/SimulationAndResultArea/SimulationEmptyResult'
import { SimulationProgressing } from '~/pages/Simulation/components/SimulationAndResultArea/SimulationProgressing'
import { useSimulateMargin } from '~/pages/Simulation/hooks/useSimulateMargin'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'
import { useGetMe } from '~/pages/hooks/useGetMe'

export const MarginSimulationSection: React.FC = () => {
  const { t } = useTranslation(['page'])
  const { handleSubmit } = useFormContext<OrderSimulationSchema>()
  const { invokeSimulateMargin, progress, result, isProcessing } =
    useSimulateMargin()
  const { showToast: showErrorToast } = useErrorToast({
    toastId: 'MarginSimulationSection/error',
  })
  const { showToast: showSuccessToast } = useSuccessToast({
    toastId: 'MarginSimulationSection/success',
  })

  const { optionCategory } = useGetMe()

  const onSubmit = handleSubmit(async (data) => {
    await invokeSimulateMargin({
      value: data,
      onSuccess: () => {
        showSuccessToast({
          toastMessage: t('page:simulation.simulation_success'),
        })
      },
      onError: (error: string) => {
        showErrorToast({
          toastMessage: error,
        })
      },
    })
  })

  return (
    <Stack gap="4">
      <Stack gap="2">
        <Text textStyle="headerMd">
          {optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_1 && (
            <Text textStyle="headerMd">
              {t('page:simulation.margin_simulation_title_option_1')}
            </Text>
          )}
          {optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_2 && (
            <Text textStyle="headerMd">
              {t('page:simulation.margin_simulation_title_option_2')}
            </Text>
          )}
        </Text>
        <ColumnContent
          label={t('page:simulation.select_simulation')}
          headerSize="Xs"
        >
          <MarginSimulationRelationForm />
        </ColumnContent>
      </Stack>
      <Box width="full" display="flex" justifyContent="end">
        <FormPrimaryButton
          onClick={onSubmit}
          data-gtm-button={EVENT_NAME.BUTTON_MARGIN_SIMULATION}
        >
          {t('page:simulation.run_simulation')}
        </FormPrimaryButton>
      </Box>
      <Stack gap="2">
        <Stack gap="1">
          <Text textStyle="headerXs">
            {t('page:simulation.simulation_result')}
          </Text>
          <Text textStyle="textSm" color="Text.midEmphasis">
            {t('page:simulation.margin_simulation_description')}
          </Text>
        </Stack>
        {!isProcessing && result === undefined && <SimulationEmptyResult />}
        {isProcessing && <SimulationProgressing progress={progress} />}
        {!isProcessing && result !== undefined && (
          <MarginSimulationTable result={result} />
        )}
      </Stack>
    </Stack>
  )
}
